import { useState } from 'react';

import { Button } from '../../../shared/ui/button';
import { GROUP_LABEL } from '../../../shared/ui/group-label';
import { PlusIcon, SearchIcon } from '../../../shared/ui/icons';
import { Input } from '../../../shared/ui/input';
import { Modal } from '../../../shared/ui/modal';
import { Note } from '../../../shared/ui/note';
import { ErrorState, LoadingState } from '../../../shared/ui/states';
import {
  ReplayNote,
  senderReplayable,
  sourcesUsing,
  type TSender,
  type TSenderSearch,
} from '../../../entities/auth-preset';
import type { IStagedSender } from '../use-bind-sender';
import { useSenderSearch } from '../use-sender-search';
import { SenderRow } from './sender-row';

const Band = ({ label, count }: { label: string; count?: string }) => (
  <div className="flex items-baseline gap-2 px-4 pt-3 pb-1">
    <span className={GROUP_LABEL}>{label}</span>
    {count && <span className="font-mono text-micro text-faint">{count}</span>}
  </div>
);

// Under the list, not in the footer: it is about the list, and in the footer it
// squeezed three buttons into a column.
const listNote = (data: TSenderSearch | null, typed: boolean): string | null => {
  if (!data || data.noMatch || typed) return null;

  const hidden = data.total - data.items.length;
  if (hidden > 0) return `${hidden.toLocaleString('en-US')} more — start typing`;
  return `${data.total.toLocaleString('en-US')} senders · kept up to date by us`;
};

interface ISenderPickerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  currentPresetId: string | null;
  stagedPresetId: string | null;
  onStage: (next: IStagedSender | null) => void;
  onByHand: () => void;
}

export const SenderPicker = ({
  open,
  onOpenChange,
  currentPresetId,
  stagedPresetId,
  onStage,
  onByHand,
}: ISenderPickerProps) => {
  const [query, setQuery] = useState('');
  const [chosenId, setChosenId] = useState<string | null>(null);

  const search = useSenderSearch(query);
  const data = search.data ?? null;
  const items = data?.items ?? [];

  const close = () => {
    setQuery('');
    setChosenId(null);
    onOpenChange(false);
  };

  // Never falls back to the first row: Enter would then use a sender nobody picked.
  const chosen: TSender | null =
    items.find((row) => row.presetId === chosenId) ??
    items.find((row) => row.presetId === (stagedPresetId ?? currentPresetId)) ??
    null;

  // Re-confirming the sender in force stages null on purpose: staging it would repin onto the latest version.
  const isCurrent = chosen !== null && chosen.presetId === currentPresetId;
  const commit = () => {
    if (!chosen) return;
    onStage(
      isCurrent
        ? null
        : { presetId: chosen.presetId, versionId: chosen.latestVersionId, name: chosen.name },
    );
    close();
  };

  const typed = query.trim().length > 0;
  const noMatch = data?.noMatch ?? null;
  const byHeader = data?.matchedBy === 'header';
  const used = items.filter((row) => row.usedByActiveSourceCount > 0);
  const rest = items.filter((row) => row.usedByActiveSourceCount === 0);
  const sources = sourcesUsing(used);
  const note = listNote(data, typed);

  const walk = (step: number) => {
    const at = items.findIndex((row) => row.presetId === chosen?.presetId);
    const next = items[Math.min(Math.max(at + step, 0), items.length - 1)];
    if (next) setChosenId(next.presetId);
  };

  return (
    <Modal
      open={open}
      onOpenChange={(next) => (next ? onOpenChange(true) : close())}
      title="Who sends to this source?"
      description="Pick them and the whole scheme comes with it."
      width="lg"
      bleed
      footer={
        noMatch ? undefined : (
          <Button variant="ghost" size="sm" onClick={onByHand}>
            Set it up by hand
          </Button>
        )
      }
      submit={
        noMatch
          ? undefined
          : {
              label: chosen ? `${isCurrent ? 'Keep' : 'Use'} ${chosen.name}` : 'Use',
              onSubmit: commit,
              disabled: chosen === null,
            }
      }
    >
      <div className="px-4 pt-3 pb-1">
        <Input
          aria-label="Find the sender"
          leading={<SearchIcon size={15} />}
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'ArrowDown') walk(1);
            else if (event.key === 'ArrowUp') walk(-1);
            // Bare Enter only: ⌘↵ bubbles to the dialog, and both would commit.
            else if (event.key === 'Enter' && !event.metaKey && !event.ctrlKey && chosen) commit();
            else return;
            event.preventDefault();
          }}
          placeholder="Name, old name, domain"
          autoFocus
        />
      </div>

      {search.isError && (
        <div className="px-4 py-3">
          <ErrorState error={search.error} onRetry={() => void search.refetch()} />
        </div>
      )}

      {search.isPending && !search.isError && <LoadingState rows={4} className="px-4 py-3" />}

      {data && !search.isError && (
        <>
          <div className="max-h-body overflow-y-auto">
            {typed && items.length > 0 && (
              <>
                <Band
                  label={byHeader ? 'Senders using that header' : 'Senders'}
                  count={data.total.toLocaleString('en-US')}
                />
                {items.map((row) => (
                  <SenderRow
                    key={row.presetId}
                    sender={row}
                    query={query}
                    withShape={byHeader}
                    selected={row.presetId === chosen?.presetId}
                    onSelect={() => setChosenId(row.presetId)}
                  />
                ))}
              </>
            )}

            {!typed && (
              <>
                {used.length > 0 && (
                  <>
                    <Band
                      label="Already used here"
                      count={`${sources.toLocaleString('en-US')} ${sources === 1 ? 'source' : 'sources'}`}
                    />
                    {used.map((row) => (
                      <SenderRow
                        key={row.presetId}
                        sender={row}
                        query=""
                        withShape={false}
                        selected={row.presetId === chosen?.presetId}
                        onSelect={() => setChosenId(row.presetId)}
                      />
                    ))}
                  </>
                )}
                {rest.length > 0 && (
                  <>
                    <Band label={used.length > 0 ? 'The rest, alphabetically' : 'Senders'} />
                    {rest.map((row) => (
                      <SenderRow
                        key={row.presetId}
                        sender={row}
                        query=""
                        withShape={false}
                        selected={row.presetId === chosen?.presetId}
                        onSelect={() => setChosenId(row.presetId)}
                      />
                    ))}
                  </>
                )}
              </>
            )}
            {note && <p className="px-4 pt-2 pb-3 font-mono text-micro text-faint">{note}</p>}
          </div>

          {noMatch && (
            <>
              <div className="px-4 pt-5 pb-4 text-center">
                <p className="text-body leading-20 text-primary">
                  No sender called {noMatch.query} yet.
                </p>
                <p className="mt-2 text-data text-muted">
                  For a sender that signs the ordinary way.
                </p>
              </div>
              <div className="px-4 pb-4">
                <Button block onClick={onByHand}>
                  <PlusIcon size={13} />
                  Set it up by hand
                </Button>
              </div>
            </>
          )}

          {chosen && (senderReplayable(chosen) || chosen.confidence === 'medium') && (
            <div className="flex flex-col gap-2 px-4 pt-3 pb-4">
              {senderReplayable(chosen) && <ReplayNote name={chosen.name} />}

              {chosen.confidence === 'medium' && (
                <Note>
                  We are less sure we read {chosen.name}’s own documentation right. The scheme is
                  used as it stands
                  {chosen.docsUrl ? (
                    <>
                      {' — '}
                      <a
                        href={chosen.docsUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="text-primary underline"
                      >
                        their signing docs
                      </a>{' '}
                      are what to check it against if a request is rejected.
                    </>
                  ) : (
                    '.'
                  )}
                </Note>
              )}
            </div>
          )}
        </>
      )}
    </Modal>
  );
};
